"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, ArrowRight, Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
const contactItems = [{
  title: "Email Us 24/7",
  subtitle: "ks@theksway.com",
  description: "We're always here to answer your questions. Reach us anytime via email.",
  link: "mailto:ks@theksway.com",
  icon: <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path d="M0.8,3.2C0.5,3.5,0.3,3.9,0.3,4.4v11.2c0,1,0.8,1.7,1.7,1.7h15.9c1,0,1.7-0.8,1.7-1.7V4.4c0-0.5-0.2-0.9-0.5-1.2l-8.6,9 c0,0,0,0,0,0c-0.1,0.1-0.3,0.2-0.5,0.2c-0.2,0-0.4-0.1-0.5-0.2c0,0,0,0,0,0L0.8,3.2z" />
        <path d="M9.9,11.7c0,0,0.1,0,0.1,0l8.6-8.9c-0.2-0.1-0.4-0.1-0.7-0.1H2.1c-0.2,0-0.5,0-0.7,0.1L9.9,11.7z" />
      </svg>
}, {
  title: "Our Location",
  subtitle: "Aisopou 17, Agios Nikolaos, 3100, Limassol",
  description: "Find us in the heart of the city, easy to access from all main routes.",
  icon: <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <path d="M50,1.5C30.9,1.5,15.4,17,15.4,36.1C15.4,55.3,50,98.5,50,98.5s34.6-43.2,34.6-62.4C84.6,17,69.1,1.5,50,1.5z M50,49.2 c-8.3,0-15-6.7-15-15s6.7-15,15-15s15,6.7,15,15S58.3,49.2,50,49.2z" />
      </svg>
}, {
  title: "Call Us",
  subtitle: "+357 97 673 308",
  description: "Our support team is available day and night to assist you by phone.",
  link: "tel:0035797673308",
  icon: <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path d="M11,14.2c-1.9-1.1-4.2-4.3-4.7-6.3c0.8-1.3,1.5-0.7,2-2c0-0.1,1.2-6.6-3.4-5.3c-10,3,6.5,25.3,12.8,17.2 c2.8-3.8-4-4.8-4.1-4.8C12.1,13,12.5,13.8,11,14.2z" />
      </svg>
}];
const ContactInfoCard = ({
  icon,
  title,
  subtitle,
  description,
  link
}) => {
  return <div className="bg-white rounded-[40px] flex flex-col h-full shadow-[0_10px_50px_rgba(0,0,0,0.05)] border border-gray-50 !items-center !justify-center ![padding:40px_40px_20px_40px]">
      <div className="flex items-center gap-5 mb-6">
        <div className="size-16 lg:size-20 rounded-full bg-[#f1f1f1] flex items-center justify-center shrink-0">
          <div className="size-8 lg:size-10 text-[#C69A59] fill-current">{icon}</div>
        </div>
        <div>
          <h5 className="text-xl lg:text-2xl font-bold text-[#161616]">
            {link ? <Link to={link} className="hover:text-[#C69A59] transition-colors">{title}</Link> : title}
          </h5>
          <div className="text-[#C69A59] font-medium text-base lg:text-lg">{subtitle}</div>
        </div>
      </div>
    </div>;
};
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-12 bg-[#C69A59]/5 border border-[#C69A59]/20 rounded-2xl text-center max-w-2xl mx-auto">
        <div className="size-16 bg-[#C69A59] rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="text-white size-8" />
        </div>
        <h3 className="text-2xl font-display font-bold text-[#161616] mb-4">Message Sent!</h3>
        <p className="text-[#707070] text-lg mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-full border-[#C69A59] text-[#C69A59] hover:bg-[#C69A59] hover:text-white transition-all">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input placeholder="Your Name*" className="h-14 bg-white border-[#EDEDED] rounded-md focus:border-[#C69A59] focus:ring-0 transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input type="email" placeholder="Email Address*" className="h-14 bg-white border-[#EDEDED] rounded-md focus:border-[#C69A59] focus:ring-0 transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input placeholder="Project Type*" className="h-14 bg-white border-[#EDEDED] rounded-md focus:border-[#C69A59] focus:ring-0 transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormControl>
                <Input placeholder="Phone Number*" className="h-14 bg-white border-[#EDEDED] rounded-md focus:border-[#C69A59] focus:ring-0 transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormControl>
              <Textarea placeholder="Your Message*" className="min-h-40 bg-white border-[#EDEDED] rounded-md focus:border-[#C69A59] focus:ring-0 transition-all resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-100 rounded-md text-red-600 text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="inline-flex items-center justify-center gap-2 whitespace-nowrap [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs has-[>svg]:px-3 h-16 rounded-full bg-[#C69A59] text-white font-bold text-lg transition-all duration-300 group w-full ![padding:24px_18px_24px_24px] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-[#161616] md:w-auto">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : <>
            Submit Message
            <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
          </>}
      </Button>
    </Form>;
}
function ContactFormContent() {
  return <div className="space-y-24 lg:space-y-32">
      {/* Contact Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {contactItems.map((item, index) => <ContactInfoCard key={index} {...item} />)}
      </div>

      {/* Map + Form */}
      <div className="grid grid-cols-1 gap-16 items-start ![margin:-50px_0px_0px_0px] lg:grid-cols-2 lg:gap-24">
        {/* Left: text + map */}
        <div className="space-y-8">
          <div className="inline-block px-4 py-1 rounded-full bg-[#C69A59]/10 text-[#C69A59] text-xs font-bold uppercase tracking-widest">
            Quick Support
          </div>
          <h2 className="font-display font-bold text-[#161616] leading-tight !text-3xl">
            Get in Touch with Our Experts
          </h2>
          <p className="text-[#707070] text-lg leading-relaxed max-w-lg">
            Whether you have a question about features, pricing, or anything else, our team is ready
            to answer all your questions.
          </p>
          <div className="pt-8">
            <div className="w-full aspect-video rounded-2xl shadow-2xl overflow-hidden">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3271.642597950943!2d33.041695!3d34.688265!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14e7330d66c3033d%3A0x6e788649866e4e5e!2sAisopou%2017%2C%20Limassol!5e0!3m2!1sen!2scy!4v1715684321000!5m2!1sen!2scy" className="w-full h-full border-0 grayscale hover:grayscale-0 transition-all duration-700" allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="space-y-8">
          <div className="inline-block px-4 py-1 rounded-full bg-[#C69A59]/10 text-[#C69A59] text-xs font-bold uppercase tracking-widest">
            Comment Form
          </div>
          <h2 className="font-display font-bold text-[#161616] leading-tight !text-3xl">
            Send a Message
          </h2>

          <FormProvider formId="contact_form_main" sectionName="Contact Form Main" defaultValues={{
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          message: {
            required: true,
            minLength: 10
          }
        }} successMessage="Thank you for your message! Our team will contact you shortly.">
            <ContactFormInner />
          </FormProvider>
        </div>
      </div>
    </div>;
}
export default function ContactForm() {
  return <section id="contact-form" className="bg-[#f7f7f7] overflow-hidden ![padding:80px_24px_128px_24px]">
      <div className="max-w-7xl mx-auto">
        <ContactFormContent />
      </div>
    </section>;
}